import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import fallback from "@insightfulscience/shared-utils/async/fallback";
import then from "@insightfulscience/shared-utils/async/then";
import { compose } from "@insightfulscience/shared-utils/fn";
import ActionFailException from "../exceptions/ActionFailException";

const actionFallback = setResultStatus =>
	fallback(err => {
		if (!(err instanceof ActionFailException)) {
			// rethrowing err if it's not an ActionFailException
			throw err;
		}

		// suppressing ActionFailException
		setResultStatus({ errors: err, hasErrors: true });
	});

const actionSuccess = setResultStatus =>
	then(result => {
		setResultStatus({ result, errors: {}, hasErrors: false });
		return result;
	});

const actionEnhancer = setResultStatus =>
	compose(
		actionFallback(setResultStatus), // first decorator
		actionSuccess(setResultStatus), // second decorator
	);

const getInitialState = () => ({ errors: {}, hasErrors: false, result: undefined });

const useUniEffect = useEffect;

function useResultStatus(action) {
	const unmounted = useRef(false);
	const [resultStatus, setResultStatus] = useState(getInitialState);
	const statusRef = useRef();
	statusRef.current = resultStatus;

	useUniEffect(
		() => () => {
			unmounted.current = true;
		},
		[],
	);

	const updateResultStatus = useCallback(result => {
		if (unmounted.current) return;
		setResultStatus(result);
	}, []);

	const withResultStatus = useMemo(
		() =>
			actionEnhancer(updateResultStatus)(
				// fallback decorator
				(...args) => {
					setResultStatus(getInitialState);
					return action(...args);
				},
			),
		[action], // memoization dependencies
	);

	const resetResult = useCallback(
		() => {
			if (statusRef.current.hasErrors) {
				// if there is no error, so there is no need to reset status
				setResultStatus(getInitialState);
			}
		},
		[], // deps
	);

	return [withResultStatus, resultStatus, resetResult];
}

export default useResultStatus;
